/**
 * Order review step (OCP-526) - the last thing a customer sees before an order
 * leaves our surface.
 *
 * AGB V4 (OWS-D23):
 *   1.1 offers are non-binding; the customer makes the offer by clicking the
 *       button "Kostenpflichtig Buchen"; the contract comes into effect through
 *       our order confirmation or through provision of the service,
 *   1.2 the services and their costs follow from the offer - which is why the
 *       portal states the services ordered, the price, the billing cycle and
 *       the term before the order (product rule from the superseded draft
 *       OWS-D6 § 2 (3)).
 *
 * The product used to do none of that: the CTA said "Buy" / "Subscribe" and
 * jumped straight to the Merchant of Record, so the customer never saw on OUR
 * surface what they were about to order. This component is that missing step.
 *
 * Usage - one hook, all checkout entry points:
 *
 *   const order = useOrderReview();
 *   ...
 *   order.start({ productId, onOrder: ({ seatCount }) => runCheckout(...) });
 *   ...
 *   {order.element}
 *
 * Design notes that are load-bearing rather than cosmetic:
 *
 *  - THE SUMMARY IS SERVED, NOT COMPOSED HERE. `GET /v1/portal/checkout/preview`
 *    returns the same quote object the API snapshots onto the order record when
 *    the order is placed. If this component built its own summary out of
 *    catalog fields, the screen and the evidence could drift - which is exactly
 *    what 1.2 forbids.
 *  - THE BUTTON LABEL COMES FROM THE SERVER for the same reason, and because
 *    the wording is an open legal question (OWS-168, § 312j (3) BGB). It lives
 *    in ONE constant in `apps/api/src/services/orders.ts`; the fallback below
 *    exists only for a preview call that failed and must stay identical to it.
 *  - ONE DIALOG, NOT TWO. When the account still owes the responsible contact
 *    person (OCP-524) or the business-capacity declaration (OCP-534), those
 *    forms are steps INSIDE this dialog rather than modals stacked in front of
 *    it. Naming the contact, declaring business capacity and placing the order
 *    are one moment in the flow.
 *  - The ordering button sits DIRECTLY under the summary with nothing between
 *    them (§ 312j (2) BGB requires the essentials immediately above it). This
 *    is why the Präambel B declaration is rendered as the LAST ROW OF THE SUMMARY
 *    BLOCK rather than between the block and the button.
 *  - OCP-534: orboto contracts exclusively with entrepreneurs (Präambel B), so an
 *    account an operator has refused sees an explanation instead of an ordering
 *    button. The customer is always told why.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '../i18n';

import { api, ApiError } from '../lib/api';
import {
  BusinessCapacityForm,
  BusinessCapacityRefusalNotice,
  formatDeclarationDate,
  type BusinessCapacityResponse,
} from './BusinessCapacityGate';
import { ResponsibleContactForm } from './ResponsibleContactGate';
import { forgetDiscountCode, rememberedDiscountCode } from '../lib/discount-code-carry';
import { fmtNumber } from '../lib/format-money';
import { TermsAcceptanceForm, formatTermsDate, pendingAvv, termsTextHref, type TermsStatusResponse } from './TermsAcceptanceGate';
import { servicePricesHref } from '../routes/ServicePricesPage';

/** The order summary, exactly as `OrderService.buildQuote` produces it. */
export interface OrderQuote {
  product_id: string;
  product_display_name: string;
  product_family: string;
  product_description: string | null;
  service_bullets: string[];
  billing_period: string;
  billing_cycle_label: string;
  term_note: string;
  seat_based: boolean;
  seat_count: number;
  unit_amount_cents: number;
  /** OCP-991 - after the discount; `subtotal_amount_cents` is before it. */
  total_amount_cents: number;
  subtotal_amount_cents?: number;
  /** OCP-991 - the code on this summary, null / absent without one. */
  discount?: {
    code: string;
    percent: number;
    duration: 'once' | 'forever' | 'repeating';
    duration_in_months: number | null;
    amount_cents: number;
    note: string;
  } | null;
  /** OCP-991 - only Team and Business take a code; absent on an older API. */
  discount_code_allowed?: boolean;
  currency: string;
  tax_note: string;
  payment_required: boolean;
  order_button_label: string;
  seat_ceiling: number;
  /** OCP-660 - present on orboto Token pack orders (always on since OCP-668). */
  token_pack?: { tokens: number; validity_days: number | null } | null;
  /**
   * OCP-851 - the service price list (AGB V4 6.6) in force: named and linked
   * on the summary so a 6.6 charge follows from the offer. Null / absent when
   * none is published - the line is omitted, ordering is not blocked.
   */
  service_price_list?: { version: string; effective_from: string } | null;
  /**
   * OCP-1036 - the company package agreed with this account: it fixes the seat
   * count and the per-seat price. Null / absent on every list-price summary.
   */
  company_package?: {
    id: string;
    seat_count: number;
    price_per_seat_cents: number;
    total_per_period_cents: number;
    term_months: number;
    includes_onboarding: boolean;
    includes_training: boolean;
    customer_note: string | null;
    note: string;
  } | null;
}

/** Preview refusals that leave a remembered discount code alone (see `load`). */
const KEEP_REMEMBERED_ON: ReadonlySet<string> = new Set(['discount_code_wrong_plan', 'discount_code_not_available']);

export interface CheckoutPreviewResponse {
  quote: OrderQuote;
  /**
   * OCP-991 - why the entered code does not apply, null when it does. The quote
   * then stands undiscounted, so the customer can correct it or order without.
   */
  discount_error?: string | null;
  /** OCP-524 - false means the contact form is one of this dialog's steps. */
  responsible_contact_named: boolean;
  /**
   * OCP-534 - the Präambel B business-capacity state, served with the summary so
   * ONE round-trip drives ONE dialog. `can_conclude: false` adds a step (or,
   * for an operator refusal, replaces the ordering button with the reason).
   *
   * Optional so a rolling deploy (new SPA, API not yet updated) degrades to the
   * pre-OCP-534 flow instead of a blank dialog. The server-side gate stays
   * authoritative either way: an order that should have been blocked is still
   * blocked, and `contractGateMessage()` turns that into actionable copy.
   */
  business_capacity?: BusinessCapacityResponse;
  /**
   * OCP-525 - the Terms acceptance state, served with the summary. When the
   * account has not accepted the CURRENT version the dialog adds a step;
   * `unpublished` replaces the ordering button with an explanation. Optional
   * for the same rolling-deploy reason as `business_capacity`.
   */
  terms?: TermsStatusResponse;
}

export const CHECKOUT_PREVIEW_PATH = '/v1/portal/checkout/preview';

/**
 * Offline fallback for the ordering-button label, used ONLY when the preview
 * call failed and we still have to render something.
 *
 * >>> Keep byte-identical to `ORDER_BUTTON_LABEL` in
 * >>> `apps/api/src/services/orders.ts`, which is the single source of truth.
 * >>> The wording itself is an open legal decision - see OWS-168 (§ 312j (3)
 * >>> BGB, Button-Loesung). Change it THERE, not here.
 */
export const FALLBACK_ORDER_BUTTON_LABEL = 'Kostenpflichtig Buchen';

/**
 * OCP-656: the fallback from the catalogue. "Kostenpflichtig Buchen" in
 * BOTH languages - the label AGB V4 clause 1.1 names (OWS-D23), taken 1:1
 * (operator 2026-10-17). The served `order_button_label` still wins.
 */
export function fallbackOrderButtonLabel(): string {
  return i18n.t('order-review:fallbackOrderButton', { defaultValue: FALLBACK_ORDER_BUTTON_LABEL });
}

export interface OrderReviewRequest {
  /** Catalog product the customer picked. */
  productId: string;
  /** Starting seat count for seat-based products. Default 1. */
  seatCount?: number;
  /**
   * Runs when the customer clicks the ordering button. Receives the seat count
   * that was on screen, so the caller posts the checkout the customer actually
   * confirmed. Typically ends in a redirect, so the dialog stays open (with the
   * button in its pending state) until the page navigates away.
   * OCP-991 - `discountCode` is the code on the summary, for the caller to post.
   */
  onOrder: (input: { productId: string; seatCount: number | null; discountCode: string | null }) => Promise<void> | void;
}

export interface OrderReview {
  /** Open the review dialog for one product. */
  start: (request: OrderReviewRequest) => void;
  /** Render this somewhere in the page - it is the dialog. */
  element: React.ReactNode;
}

export function useOrderReview(): OrderReview {
  const [request, setRequest] = useState<OrderReviewRequest | null>(null);

  const close = useCallback(() => setRequest(null), []);

  return {
    start: useCallback((next: OrderReviewRequest) => setRequest(next), []),
    element: request ? (
      <OrderReviewDialog request={request} onCancel={close} />
    ) : null,
  };
}

/**
 * `refused` is terminal: an operator has refused further contract conclusion on
 * this account (AGB V4 Präambel B), and that is not something the customer can
 * resolve inside the dialog.
 */
type Step = 'loading' | 'contact' | 'capacity' | 'terms' | 'review' | 'refused' | 'unpublished' | 'error';

/**
 * Which collection steps this order still needs, in the order they are shown.
 * Derived from ONE preview response so the "Step n of m" counter and the step
 * machine can never disagree.
 */
function pendingSteps(preview: CheckoutPreviewResponse | null): Step[] {
  if (!preview) return [];
  const steps: Step[] = [];
  if (!preview.responsible_contact_named) steps.push('contact');
  const blocked = preview.business_capacity?.blocked_reason;
  if (blocked === 'business_capacity_missing' || blocked === 'company_name_missing') {
    steps.push('capacity');
  }
  // OCP-525 - last collection step: the binding offer is made against the
  // current Terms, so they are confirmed right before the summary.
  // OCP-847 - and against the AVV in force: the step also opens when only the
  // AVV is not accepted (`can_conclude` covers both documents).
  if (preview.terms && preview.terms.state !== 'unpublished' && (preview.terms.state !== 'accepted' || pendingAvv(preview.terms))) {
    steps.push('terms');
  }
  return steps;
}

/**
 * Which step to show for a given preview. An operator refusal (AGB V4 Präambel B,
 * B2B only) is terminal - the customer
 * cannot resolve it in the dialog, so they get the reason instead of a form.
 */
function nextStepFrom(preview: CheckoutPreviewResponse): Step {
  if (preview.business_capacity?.blocked_reason === 'business_capacity_refused') {
    return 'refused';
  }
  if (preview.terms?.state === 'unpublished' || preview.terms?.avv?.state === 'unpublished') return 'unpublished';
  return pendingSteps(preview)[0] ?? 'review';
}

function OrderReviewDialog({
  request,
  onCancel,
}: {
  request: OrderReviewRequest;
  onCancel: () => void;
}) {
  const { t } = useTranslation('order-review');
  const [preview, setPreview] = useState<CheckoutPreviewResponse | null>(null);
  const [step, setStep] = useState<Step>('loading');
  /**
   * The steps this dialog will walk through, fixed on the FIRST successful
   * preview. Recomputing it as steps complete would make the "Step 2 of 3"
   * counter shrink under the customer's feet.
   */
  const [plan, setPlan] = useState<Step[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // OCP-851 - neutral note (not an error): the service price list changed between summary and click.
  const [notice, setNotice] = useState<string | null>(null);
  // Two pieces of seat state on purpose. `seatInput` is the raw text the
  // customer is typing (so clearing the field does not fight a controlled
  // value), `seats` is the last value that parsed into a real seat count and
  // is what we re-price against. Without the split, a controlled input bound
  // to the server-returned count makes the field un-editable mid-typing.
  const [seatInput, setSeatInput] = useState<string>(String(request.seatCount ?? 1));
  const [seats, setSeats] = useState<number>(request.seatCount ?? 1);
  const [placing, setPlacing] = useState(false);
  // OCP-991 - same split as the seats: `codeInput` is what is being typed,
  // `appliedCode` what was sent for pricing. Undefined = nothing chosen yet, so
  // the server may fill in the code the account brought from the waitlist;
  // empty = the customer removed it.
  const [codeInput, setCodeInput] = useState<string>(rememberedDiscountCode() ?? '');
  const [appliedCode, setAppliedCode] = useState<string | undefined>(rememberedDiscountCode() ?? undefined);

  const load = useCallback(
    async (seatCount: number, signal?: AbortSignal): Promise<CheckoutPreviewResponse | null> => {
      setError(null);
      try {
        const params = new URLSearchParams({ product_id: request.productId });
        params.set('seat_count', String(seatCount));
        if (appliedCode !== undefined) params.set('discount_code', appliedCode);
        const r = await api<CheckoutPreviewResponse>(
          `${CHECKOUT_PREVIEW_PATH}?${params.toString()}`,
          { signal },
        );
        setPreview(r);
        setPlan((current) => current ?? [...pendingSteps(r), 'review']);
        setStep(nextStepFrom(r));
        if (r.quote.discount) setCodeInput(r.quote.discount.code);
        // A code that can never work again must not come back on the next order.
        // Two refusals say nothing about the code itself: it is for another plan
        // (it works on the right product) or codes cannot be redeemed at the
        // moment (it works again once they can). Both keep it remembered.
        if (r.discount_error && !KEEP_REMEMBERED_ON.has(r.discount_error)) forgetDiscountCode();
        return r;
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return null;
        setStep('error');
        if (err instanceof ApiError && err.status === 400 && err.code === 'seat_count_above_max') {
          // OCP-1036 - a seat count past what one order takes (named by the server). Any other
          // 400 is not that case and takes the generic error below.
          setError(t('error.seatLimit'));
        } else if (err instanceof ApiError && err.status === 404) {
          setError(t('error.notAvailable'));
        } else if (err instanceof ApiError && err.status === 401) {
          setError(t('error.session'));
        } else if (err instanceof ApiError && err.status === 503) {
          // OCP-580 - dispute breaker tripped (or provider unconfigured):
          // payments are deliberately paused, not broken.
          setError(t('error.paymentsPaused'));
        } else {
          setError(t('error.summary'));
        }
        return null;
      }
    },
    [request.productId, appliedCode, t],
  );

  useEffect(() => {
    const ac = new AbortController();
    void load(seats, ac.signal);
    return () => ac.abort();
    // Re-runs on seat change so the summary above the ordering button always
    // matches the seat count on screen - a stale price there would be exactly
    // the 1.2 problem this step exists to fix.
  }, [load, seats]);

  const quote = preview?.quote ?? null;

  async function placeOrder() {
    if (placing || !quote) return;
    setPlacing(true);
    setError(null);
    setNotice(null);
    const offeredPriceList = quote.service_price_list?.version ?? null;
    try {
      await request.onOrder({
        productId: quote.product_id,
        seatCount: quote.seat_based ? quote.seat_count : null,
        discountCode: quote.discount?.code ?? null,
      });
      // Still here => the hand-off did not navigate away, so something was
      // refused. Re-probe rather than leave a stale dialog offering a button
      // the server just rejected: if an operator refused the account (or the
      // declaration was cleared) while this dialog was open, the reload moves
      // us to the step that explains it.
      const reloaded = await load(seats);
      // OCP-851 - the server refused the click because the price list in force is no longer the one on
      // this summary (409 service_prices_changed): say so, neutrally; the summary above is already the new one.
      if (reloaded && (reloaded.quote.service_price_list?.version ?? null) !== offeredPriceList) {
        setNotice(t('priceListChanged'));
      }
    } finally {
      // The caller normally redirects; if it returned instead (an error it
      // rendered on the page behind us) let the customer retry or close.
      setPlacing(false);
    }
  }

  const heading = t('heading');

  /**
   * Move the dialog on after a step completed, from the UPDATED preview rather
   * than from a guess. Keeps the step machine and the served state in sync
   * without a second round-trip.
   */
  function advance(next: CheckoutPreviewResponse) {
    setPreview(next);
    setStep(nextStepFrom(next));
  }

  /** "Step 2 of 3." - omitted entirely when the review IS the only step. */
  function stepLabel(current: Step): string | null {
    if (!plan || plan.length < 2) return null;
    const index = plan.indexOf(current);
    if (index < 0) return null;
    return t('stepLabel', { index: index + 1, total: plan.length });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={heading}
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-xl"
        data-testid="order-review-dialog"
      >
        <h2 className="text-base font-semibold">{heading}</h2>

        {step === 'loading' && (
          <p className="mt-3 text-sm text-gray-500">{t('loading')}</p>
        )}

        {step === 'error' && (
          <>
            <div
              role="alert"
              className="mt-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
            >
              {error}
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={onCancel}
                className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
              >
                {t('close')}
              </button>
            </div>
          </>
        )}

        {/* An operator has refused further contract conclusion on this account
            (AGB V4 Präambel B). Terminal: no ordering button, and the customer is
            told why rather than meeting a button that does nothing. */}
        {step === 'refused' && preview && (
          <>
            <BusinessCapacityRefusalNotice
              refusal={preview.business_capacity?.refusal ?? null}
            />
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={onCancel}
                className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
              >
                {t('close')}
              </button>
            </div>
          </>
        )}

        {/* Collection step (only when the account still owes it): the
            responsible contact person the Terms require at contract
            conclusion. Same form as the Settings page, so the authorization
            wording cannot drift. */}
        {step === 'contact' && preview && (
          <>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium">{stepLabel('contact')}</span>
              {t('contactIntro')}
            </p>
            <ResponsibleContactForm
              submitLabel={t('contactSubmit')}
              onCancel={onCancel}
              onSaved={() => advance({ ...preview, responsible_contact_named: true })}
            />
          </>
        )}

        {/* Collection step (OCP-534): the Präambel B business-capacity
            declaration + the company name it is made for. Same form as the
            Settings page, so the declared wording cannot drift between
            "collected at checkout" and "corrected later". */}
        {step === 'capacity' && preview?.business_capacity && (
          <>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium">{stepLabel('capacity')}</span>
              {t('capacityIntro')}
            </p>
            <BusinessCapacityForm
              initial={preview.business_capacity}
              submitLabel={t('capacitySubmit')}
              onCancel={onCancel}
              onSaved={(bc) => advance({ ...preview, business_capacity: bc })}
            />
          </>
        )}

        {/* OCP-525 - the registry is empty: no version to accept, no order.
            A configuration fault on our side, said so plainly. */}
        {step === 'unpublished' && (
          <>
            <p className="mt-3 text-sm text-gray-700 dark:text-gray-300" data-testid="order-terms-unpublished">
              {t('unpublished')}
            </p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={onCancel}
                className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
              >
                {t('close')}
              </button>
            </div>
          </>
        )}

        {/* Collection step (OCP-525): accept the CURRENT Terms version. Same
            form as Settings -> Terms, same served statement. */}
        {step === 'terms' && preview?.terms && (
          <>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium">{stepLabel('terms')}</span>
              {preview.terms.state === 'accepted'
                ? t('termsIntroAvv')
                : (
                  <>
                    {t('termsIntro')}
                    {preview.terms.accepted_version
                      ? t('termsUpdated', { version: preview.terms.accepted_version.version })
                      : t('termsConfirm')}
                    {/* OCP-856 - AGB and AVV open: two documents, two checkboxes */}
                    {pendingAvv(preview.terms) ? t('termsAvvSeparate') : ''}
                  </>
                )}
            </p>
            {/* OCP-847 - AGB open: the AGB form carries the pending AVV (OCP-856: with
                its own mandatory checkbox); AGB accepted, AVV open: the AVV on its own. */}
            <TermsAcceptanceForm
              initial={preview.terms}
              kind={preview.terms.state === 'accepted' ? 'avv' : 'agb'}
              context="checkout"
              submitLabel={t('termsSubmit')}
              onCancel={onCancel}
              onSaved={(t) => advance({ ...preview, terms: t })}
            />
          </>
        )}

        {step === 'review' && quote && (
          <>
            {stepLabel('review') && (
              <p className="mt-2 text-xs text-gray-500">{stepLabel('review')}</p>
            )}
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {t('reviewIntro')}
            </p>

            {error && (
              <div
                role="alert"
                className="mt-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
              >
                {error}
              </div>
            )}
            {notice && (
              <div
                role="status"
                data-testid="order-price-list-changed"
                className="mt-4 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-2 text-sm text-gray-700 dark:text-gray-300"
              >
                {notice}
              </div>
            )}

            {/* OCP-991 - the code field sits ABOVE the summary: nothing may come
                between the summary block and the ordering button (§ 312j (2) BGB),
                and a changed code re-prices the summary below it. */}
            {quote.discount_code_allowed && (
              <form
                className="mt-4 flex flex-wrap items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  setAppliedCode(codeInput.trim());
                }}
              >
                <label className="flex-1 text-xs text-gray-600 dark:text-gray-400">
                  {t('discount.label')}
                  <input
                    type="text"
                    value={codeInput}
                    disabled={placing}
                    autoComplete="off"
                    maxLength={64}
                    onChange={(e) => setCodeInput(e.target.value)}
                    data-testid="order-discount-code"
                    className="mt-1 block w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 font-mono text-sm uppercase"
                  />
                </label>
                <button
                  type="submit"
                  disabled={placing}
                  data-testid="order-discount-apply"
                  className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                >
                  {t('discount.apply')}
                </button>
                {preview?.discount_error && (
                  <p role="alert" data-testid="order-discount-error" className="w-full text-xs text-red-700 dark:text-red-300">
                    {t(`discount.error.${preview.discount_error}`, { defaultValue: t('discount.error.discount_code_invalid') })}
                  </p>
                )}
              </form>
            )}

            {/* --- the summary 1.2 requires: services, price, billing
                cycle, term. Nothing may come between this block and the
                ordering button below it. --- */}
            <div
              className="mt-4 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-4"
              data-testid="order-summary"
            >
              <dl className="space-y-2 text-sm">
                <SummaryRow label={t('row.service')}>
                  <span
                    className="font-medium text-gray-900 dark:text-gray-100"
                    data-testid="order-summary-product"
                  >
                    {quote.product_display_name}
                  </span>
                </SummaryRow>

                {quote.seat_based && quote.company_package && (
                  <SummaryRow label={t('row.seats')}>
                    <span data-testid="order-summary-seats-fixed">{quote.seat_count}</span>
                  </SummaryRow>
                )}
                {quote.company_package && (
                  <SummaryRow label={t('row.companyPackage')}>
                    <span className="text-gray-700 dark:text-gray-300" data-testid="order-summary-company-package">
                      {quote.company_package.note}
                      {quote.company_package.customer_note ? ` ${quote.company_package.customer_note}` : ''}
                    </span>
                  </SummaryRow>
                )}

                {quote.seat_based && !quote.company_package && (
                  <SummaryRow label={t('row.seats')}>
                    <SeatPicker
                      value={seatInput}
                      ceiling={quote.seat_ceiling}
                      disabled={placing}
                      onChange={(raw) => {
                        setSeatInput(raw);
                        const next = Number(raw);
                        if (Number.isInteger(next) && next >= 1 && next < quote.seat_ceiling) {
                          setSeats(next);
                        }
                      }}
                    />
                  </SummaryRow>
                )}

                {/* OCP-991 - with a code the seat line belongs to the subtotal,
                    the price row is what the customer confirms. */}
                {quote.discount && (
                  <>
                    <SummaryRow label={t('row.subtotal')}>
                      <span data-testid="order-summary-subtotal">
                        {formatMoney(quote.subtotal_amount_cents ?? quote.total_amount_cents + quote.discount.amount_cents, quote.currency)}
                      </span>
                      {quote.seat_based && quote.seat_count > 1 && (
                        <span className="ml-1 text-xs text-gray-500">
                          {t('row.perSeat', { count: quote.seat_count, unit: formatMoney(quote.unit_amount_cents, quote.currency) })}
                        </span>
                      )}
                    </SummaryRow>
                    <SummaryRow label={t('row.discount', { percent: quote.discount.percent })}>
                      <span data-testid="order-summary-discount">
                        {formatMoney(-quote.discount.amount_cents, quote.currency)}
                      </span>
                      <span className="ml-1 font-mono text-xs text-gray-500">{quote.discount.code}</span>
                    </SummaryRow>
                  </>
                )}

                <SummaryRow label={t('row.price')}>
                  <span
                    className="font-medium text-gray-900 dark:text-gray-100"
                    data-testid="order-summary-price"
                  >
                    {formatMoney(quote.total_amount_cents, quote.currency)}
                  </span>
                  {!quote.discount && quote.seat_based && quote.seat_count > 1 && (
                    <span className="ml-1 text-xs text-gray-500">
                      {t('row.perSeat', { count: quote.seat_count, unit: formatMoney(quote.unit_amount_cents, quote.currency) })}
                    </span>
                  )}
                </SummaryRow>

                {quote.discount && (
                  <SummaryRow label={t('row.discountTerm')}>
                    <span className="text-gray-700 dark:text-gray-300" data-testid="order-summary-discount-note">
                      {quote.discount.note}
                    </span>
                  </SummaryRow>
                )}

                <SummaryRow label={t('row.billingCycle')}>
                  <span data-testid="order-summary-cycle">{quote.billing_cycle_label}</span>
                </SummaryRow>

                <SummaryRow label={t('row.term')}>
                  <span className="text-gray-700 dark:text-gray-300" data-testid="order-summary-term">
                    {quote.term_note}
                  </span>
                </SummaryRow>
              </dl>

              {quote.service_bullets.length > 0 && (
                <ul className="mt-3 space-y-1 border-t border-gray-200 dark:border-gray-700 pt-3 text-xs text-gray-600 dark:text-gray-400">
                  {quote.service_bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <span aria-hidden="true" className="text-brand-500">
                        ·
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}

              <p
                className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3 text-xs text-gray-600 dark:text-gray-400"
                data-testid="order-summary-tax"
              >
                {quote.tax_note}
              </p>
              {quote.product_family === 'orboto-saas' && (
                <p className="mt-2 text-xs text-gray-600 dark:text-gray-400" data-testid="order-summary-ai-token">
                  {t('aiTokenExpiry')}
                </p>
              )}
              {quote.token_pack && (
                <p className="mt-2 text-xs text-gray-600 dark:text-gray-400" data-testid="order-summary-token-pack">
                  {t('tokenPack', { tokens: fmtNumber(quote.token_pack.tokens) })}{' '}
                  {quote.token_pack.validity_days
                    ? t('tokenPackValidity', { days: quote.token_pack.validity_days })
                    : t('tokenPackExpiry')}
                </p>
              )}

              {/* OCP-534 - the Präambel B declaration, quoted back on every order
                  and snapshotted onto the order row with its timestamp. It
                  lives INSIDE the summary block on purpose: nothing may come
                  between the block and the ordering button (§ 312j (2) BGB). */}
              {preview?.business_capacity?.declaration && (
                <div
                  className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3 text-xs text-gray-600 dark:text-gray-400"
                  data-testid="order-summary-business-capacity"
                >
                  <span className="block font-medium text-gray-700 dark:text-gray-300">
                    {t('businessCapacity')}
                  </span>
                  <span className="mt-1 block">
                    {preview.business_capacity.declaration.statement}
                  </span>
                  <span className="mt-1 block text-gray-500">
                    {t('confirmedOn', {
                      date: formatDeclarationDate(preview.business_capacity.declaration.confirmed_at),
                      by: preview.business_capacity.declaration.confirmed_by,
                    })}
                    {preview.business_capacity.company_name
                      ? t('confirmedFor', { company: preview.business_capacity.company_name })
                      : ''}
                    .
                  </span>
                </div>
              )}
              {/* OCP-525 - the Terms version this offer is made against,
                  inside the summary block for the same § 312j (2) reason. */}
              {preview?.terms?.accepted_version && preview.terms.state === 'accepted' && (
                <div
                  className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3 text-xs text-gray-600 dark:text-gray-400"
                  data-testid="order-summary-terms"
                >
                  <span className="block font-medium text-gray-700 dark:text-gray-300">{t('terms')}</span>
                  <span className="mt-1 block">
                    {t('termsAccepted', { title: preview.terms.accepted_version.title, version: preview.terms.accepted_version.version })}
                    {preview.terms.accepted_at ? t('termsAcceptedOn', { date: formatTermsDate(preview.terms.accepted_at) }) : ''}.{' '}
                    <a href={termsTextHref(preview.terms.accepted_version)} target="_blank" rel="noopener" className="text-brand-500 underline">
                      {t('readText')}
                    </a>
                  </span>
                  {/* OCP-847 - the AVV in force, accepted with the same click. */}
                  {preview.terms.avv?.state === 'accepted' && preview.terms.avv.accepted_version && (
                    <span className="mt-2 block" data-testid="order-summary-avv">
                      <span className="block font-medium text-gray-700 dark:text-gray-300">{t('avv')}</span>
                      {t('termsAccepted', { title: preview.terms.avv.accepted_version.title, version: preview.terms.avv.accepted_version.version })}
                      {preview.terms.avv.accepted_at ? t('termsAcceptedOn', { date: formatTermsDate(preview.terms.avv.accepted_at) }) : ''}.{' '}
                      <a href={termsTextHref({ kind: 'avv', version: preview.terms.avv.accepted_version.version })} target="_blank" rel="noopener" className="text-brand-500 underline">
                        {t('readText')}
                      </a>
                    </span>
                  )}
                  <span className="mt-2 block">{t('documentsClick')}</span>
                </div>
              )}
              {/* OCP-851 - the service price list the offer references (AGB 6.6). Not an
                  accepted document, so it sits below the "you accept these" line. */}
              {quote.service_price_list && (
                <div
                  className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3 text-xs text-gray-600 dark:text-gray-400"
                  data-testid="order-summary-service-prices"
                >
                  <span className="block font-medium text-gray-700 dark:text-gray-300">{t('servicePrices')}</span>
                  <span className="mt-1 block">
                    {t('servicePricesLine', { version: quote.service_price_list.version, date: formatTermsDate(quote.service_price_list.effective_from) })}{' '}
                    <a href={servicePricesHref(quote.service_price_list.version)} target="_blank" rel="noopener" className="text-brand-500 underline">
                      {t('readText')}
                    </a>
                  </span>
                </div>
              )}
            </div>

            {/* The ordering button, directly under the summary. Its label is
                served by the API - see the constant note at the top. */}
            <div className="mt-4">
              <button
                type="button"
                onClick={() => void placeOrder()}
                disabled={placing}
                className="w-full rounded bg-brand-500 px-3 py-2.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
                data-testid="order-place-button"
              >
                {placing
                  ? t('placing')
                  : quote.order_button_label || fallbackOrderButtonLabel()}
              </button>
              <p className="mt-2 text-center text-xs text-gray-500">
                {quote.payment_required ? t('paymentPartner') : t('noPayment')}
              </p>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onCancel}
                disabled={placing}
                className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
              >
                {t('cancel')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2">
      <dt className="w-28 shrink-0 text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="flex-1 text-sm text-gray-800 dark:text-gray-200">{children}</dd>
    </div>
  );
}

function SeatPicker({
  value,
  ceiling,
  disabled,
  onChange,
}: {
  value: string;
  ceiling: number;
  disabled: boolean;
  onChange: (raw: string) => void;
}) {
  return (
    <input
      type="number"
      min={1}
      max={ceiling - 1}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="w-20 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm disabled:opacity-60"
      data-testid="order-summary-seats"
    />
  );
}

/**
 * Integer minor units -> "EUR 12.00". Locale-independent on purpose: the
 * amount is what the order record snapshots, and the confirmation email
 * renders it the same way.
 */
export function formatMoney(amountCents: number, currency: string): string {
  const sign = amountCents < 0 ? '-' : '';
  const abs = Math.abs(amountCents);
  const major = Math.floor(abs / 100);
  const minor = String(abs % 100).padStart(2, '0');
  return `${sign}${currency.toUpperCase()} ${major}.${minor}`;
}
